import React, { useEffect, useMemo, useState } from "react";
import { Modal, Table, Alert, Tag, Button, Flex, Space, Tooltip } from "antd";
import { EyeOutlined, InfoCircleOutlined } from "@ant-design/icons";
import MessagePreview from "./MessagePreview";
import {
  VARS,
  VAR_DESCRIPTIONS,
  PROFILE_COMPLETION_REMINDER_VARS,
} from "../../constants/variables";
import { CampaignTypeEnum } from "../../constants/enums";

interface PreviewMemberModalProps {
  open: boolean;
  onClose: () => void;
  estimatedMember: any[];
  isDetailPage?: boolean;
  messageContent?: string;
  campaignType?: CampaignTypeEnum | number | null;
}

// Same fallback chain as the "Name" column below.
const getMemberName = (record: any, fallback = "-"): string => {
  const fullName =
    `${record?.FirstName ?? ""} ${record?.LastName ?? ""}`.trim();
  return fullName || record?.Name || fallback;
};

const PreviewMemberModal: React.FC<PreviewMemberModalProps> = ({
  open,
  onClose,
  estimatedMember,
  isDetailPage,
  messageContent,
  campaignType,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [viewingMember, setViewingMember] = useState<any | null>(null);
  const isMobile = window.innerWidth < 768;
  const isProfileCompletionReminder =
    campaignType === CampaignTypeEnum.ProfileCompletionReminder;

  useEffect(() => {
    if (open) {
      setCurrentPage(1);
      setPageSize(10);
    }
  }, [open]);

  const usedVariables = useMemo(() => {
    const availableVars = isProfileCompletionReminder
      ? [...VARS, ...PROFILE_COMPLETION_REMINDER_VARS]
      : VARS;
    return availableVars.filter((v) => messageContent?.includes(v));
  }, [messageContent, isProfileCompletionReminder]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={isMobile ? 400 : 800}
      style={{ top: 24 }}
      className="gray-header-modal"
      title={
        <Flex align="center" justify="space-between">
          <Flex align="center" gap={8}>
            <span>Preview Members</span>
            <Tag color="blue">{estimatedMember.length} Members</Tag>
          </Flex>
          <Button onClick={onClose}>Close</Button>
        </Flex>
      }>
      {/* Variables used in this message, cross-referenced against VARS */}
      {usedVariables.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <div>
              <Flex align="center" gap={8}>
                <strong>Variables Used</strong>
                <Tag color="blue" style={{ margin: 0 }}>
                  {usedVariables.length}
                </Tag>
              </Flex>
              <div style={{ color: "#595959", marginTop: 2, fontSize: 13 }}>
                Detected in this message, each is replaced with the
                member&apos;s actual data when sent.
              </div>
              <Space size={[6, 6]} wrap style={{ marginTop: 6 }}>
                {usedVariables.map((v) => (
                  <Tooltip key={v} title={VAR_DESCRIPTIONS[v]}>
                    <Tag
                      style={{
                        background: "#fff",
                        borderRadius: 6,
                        fontFamily: "monospace",
                        cursor: VAR_DESCRIPTIONS[v] ? "help" : "default",
                      }}>
                      {v}
                    </Tag>
                  </Tooltip>
                ))}
              </Space>
            </div>
          }
        />
      )}

      {/* Info alert */}
      {!isDetailPage && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Member data may change if campaign criteria are updated. Please reload to refresh the estimation."
        />
      )}

      {/* Table */}
      <Table
        className="table-preview"
        size="small"
        bordered
        scroll={{ x: 600 }}
        rowKey={(_, index) => index!}
        dataSource={estimatedMember}
        pagination={{
          current: currentPage,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
          showTotal: (total, range) =>
            `Results per Page ${range[1] - range[0] + 1} of ${total} Members`,
        }}
        columns={[
          {
            title: "No",
            width: !isMobile ? 60 : "5%",
            render: (_: any, __: any, index: number) =>
              (currentPage - 1) * pageSize + index + 1,
          },
          {
            title: "Name",
            render: (_: any, record: any) => getMemberName(record),
          },

          {
            title: "Customer Contact",
            render: (_: any, record: any) =>
              record.ContactIdd && record.ContactNumber
                ? `${record.ContactIdd}${record.ContactNumber}`
                : "-",
          },
          {
            title: "Email Address",
            dataIndex: "Email",
            render: (val: string) => val || "-",
          },
          {
            title: (
              <Space size={4}>
                Message
                <Tooltip title="Preview personalized messages using each member's actual data.">
                  <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
                </Tooltip>
              </Space>
            ),
            render: (_: any, record: any) =>
              record.Content ? (
                <Button
                  type="primary"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => setViewingMember(record)}>
                  Preview
                </Button>
              ) : (
                "-"
              ),
          },
        ]}
      />

      <Modal
        title="Message Content"
        open={!!viewingMember}
        onCancel={() => setViewingMember(null)}
        footer={null}
        width={600}
        style={{ top: 0 }}
        className="gray-header-modal"
        destroyOnClose>
        {viewingMember && (
          <MessagePreview
            content={viewingMember.Content}
            image={viewingMember.Image}
            link={isProfileCompletionReminder ? undefined : viewingMember.Link}
            unsubscribeLink={
              isProfileCompletionReminder
                ? undefined
                : viewingMember.UnsubscribeLink
            }
            copyTextOk={
              isProfileCompletionReminder ? undefined : viewingMember.CopyTextOk
            }
            copyTextUnsub={
              isProfileCompletionReminder
                ? undefined
                : viewingMember.CopyTextUnsub
            }
            channel={viewingMember.Channel}
            contactName={getMemberName(viewingMember, "Member")}
            contactPhone={
              viewingMember.ContactIdd && viewingMember.ContactNumber
                ? `${viewingMember.ContactIdd}${viewingMember.ContactNumber}`
                : undefined
            }
            sendDate={viewingMember.SendDate}
          />
        )}
      </Modal>
    </Modal>
  );
};

export default PreviewMemberModal;
